import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

interface Contact {
  uuid: string
  firstname?: string
  lastname?: string
  email?: string
  phone?: string
  mobile?: string
  function?: string
  referenceExterne?: string
  isSigner?: boolean
  comment?: string
  civility?: 'Monsieur' | 'Madame'
  company?: {
    uuid: string
    name: string
  }
  source?: {
    name: string
  }
}

interface PaginatedContacts {
  data: Contact[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface ContactFilters {
  firstname?: string
  lastname?: string
  email?: string
  companyUuid?: string[]
  isSigner?: boolean
  search?: string
  sort?: { column: string; direction: 'asc' | 'desc' }
}

export const useContactStore = defineStore('contact', () => {
  const suiteStore = useSuiteStore()
  const contacts = ref<PaginatedContacts | null>({
    data: [],
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  })
  const isLoadingContacts = ref(false)

  const fetchContacts = async (filters: ContactFilters = {}, page = 1, limit = 20) => {
    isLoadingContacts.value = true
    contacts.value = {
      data: [],
      page,
      limit,
      total: 0,
      totalPages: 0,
    }

    console.log(filters)
    const body = {
      ...filters,
      page,
      limit,
    }

    try {
      const response = await suiteStore.suiteFetch('contacts/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      })

      if (response !== false && response.ok) {
        const data = await response.json()
        contacts.value = {
          data: Array.isArray(data.data) ? data.data : (data.data ?? []),
          total: data?.total ?? 0,
          page: data?.page ?? page,
          limit: data?.limit ?? limit,
          totalPages: data?.totalPages ?? 1,
        }
        return contacts.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des contacts:', error)
    } finally {
      isLoadingContacts.value = false
    }
    return null
  }

  const searchCompanies = async (query: string) => {
    try {
      const response = await suiteStore.suiteFetch('companies/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: query }),
        credentials: 'include',
      })

      if (response !== false && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de sociétés:', error)
    }
    return []
  }

  const searchContacts = async (query: string) => {
    try {
      const response = await suiteStore.suiteFetch('contacts/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstname: query, lastname: query }),
        credentials: 'include',
      })

      if (response !== false && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de sociétés:', error)
    }
    return []
  }

  const currentContact = ref<Contact | null>(null)
  const isLoadingContact = ref(false)

  const fetchContact = async (contactUuid: string): Promise<Contact | null> => {
    isLoadingContact.value = true
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (response !== false && response.ok) {
        const data = await response.json()
        currentContact.value = data
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du contact:', error)
    } finally {
      isLoadingContact.value = false
    }
    return null
  }

  const updateContact = async (
    contactUuid: string,
    payload: {
      firstname?: string
      lastname?: string
      email?: string
      phone?: string
      mobile?: string
      function?: string
      civility?: 'Monsieur' | 'Madame'
      isSigner?: boolean
      comments?: string
      active?: boolean
    },
  ): Promise<{ success: true } | { success: false; message: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      })
      if (response !== false && response.ok) {
        const data = await response.json()
        if (currentContact.value?.uuid === contactUuid) currentContact.value = { ...currentContact.value, ...data }
        return { success: true }
      }
      let message = 'Erreur lors de la mise à jour.'
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? 'Erreur lors de la mise à jour.' }
    }
  }

  const fetchBrevoEmails = async (email: string, params?: { startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
    const search = new URLSearchParams()
    search.set('email', email)
    if (params?.startDate) search.set('startDate', params.startDate)
    if (params?.endDate) search.set('endDate', params.endDate)
    if (params?.limit != null) search.set('limit', String(params.limit))
    if (params?.offset != null) search.set('offset', String(params.offset))
    const response = await suiteStore.suiteFetch(`brevo/emails?${search.toString()}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (response !== false && response.ok) return response.json()
    return { emails: [], count: 0 }
  }

  const sendWelcomeEmail = async (
    contactUuid: string,
  ): Promise<
    | { success: true }
    | {
        success: false
        message: string
      }
  > => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/welcome-email`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response !== false && response.ok) return { success: true }
      let message = "Erreur lors de l'envoi."
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? "Erreur lors de l'envoi." }
    }
  }

  const sendPasswordForgotEmail = async (
    contactUuid: string,
  ): Promise<
    | { success: true }
    | {
        success: false
        message: string
      }
  > => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/password-forgot-email`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response !== false && response.ok) return { success: true }
      let message = "Erreur lors de l'envoi."
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? "Erreur lors de l'envoi." }
    }
  }

  const fetch2FAStatus = async (
    contactUuid: string,
  ): Promise<{
    type: 'APP' | 'DISABLED' | null
    disabledAt?: string
  } | null> => {
    const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/2fa-status`, {
      method: 'GET',
      credentials: 'include',
    })
    if (response !== false && response.ok) return response.json()
    return null
  }

  const reset2FA = async (contactUuid: string): Promise<{ success: true } | { success: false; message: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/reset-2fa`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response !== false && response.ok) return { success: true }
      let message = 'Erreur lors de la réinitialisation 2FA.'
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? 'Erreur lors de la réinitialisation 2FA.' }
    }
  }

  const disable2FA = async (contactUuid: string): Promise<{ success: true } | { success: false; message: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/disable-2fa`, {
        method: 'POST',
        credentials: 'include',
      })
      if (response !== false && response.ok) return { success: true }
      let message = 'Erreur lors de la désactivation 2FA.'
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? 'Erreur lors de la désactivation 2FA.' }
    }
  }

  const fetchResetTokens = async (contactUuid: string) => {
    const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/reset-tokens`, {
      method: 'GET',
      credentials: 'include',
    })
    if (response !== false && response.ok) return response.json()
    return []
  }

  const fetchPermissions = async (contactUuid: string) => {
    const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/permissions`, {
      method: 'GET',
      credentials: 'include',
    })
    if (response !== false && response.ok) return response.json()
    return []
  }

  const setPermissions = async (
    contactUuid: string,
    permissions: { companyUuid: string; codes: string[] }[],
  ): Promise<{ success: true } | { success: false; message: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/${contactUuid}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions }),
        credentials: 'include',
      })
      if (response !== false && response.ok) return { success: true }
      let message = 'Erreur lors de la mise à jour des permissions.'
      if (response !== false && !response.ok) {
        try {
          const err = await response.json()
          message = err?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? 'Erreur.' }
    }
  }

  const generateTricksterToken = async (
    contactUuid: string,
  ): Promise<{
    success: boolean
    message?: string
  }> => {
    try {
      const response = await suiteStore.suiteFetch(`auth/contact/trickster/${contactUuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })

      if (response !== false && response.ok) {
        const data = await response.json()
        const { token } = data
        const baseUrl = (import.meta.env.VITE_PORTAL_CLIENT_URL ?? '').replace(/\/+$/, '')
        const tricksterUrl = `${baseUrl}/login/as/${token}`
        window.open(tricksterUrl, '_blank')

        return { success: true }
      }

      return { success: false, message: 'Erreur.' }
    } catch (error) {
      return { success: false, message: (error as Error)?.message ?? 'Erreur.' }
    }
  }

  const createSuiteContact = async (
    companyUuid: string,
    contactData: {
      firstname: string
      lastname: string
      email: string
      mobile?: string
      phone?: string
      function?: string
      civility?: 'Monsieur' | 'Madame'
      isSigner?: boolean
      comment?: string
      portalPermissions?: string[]
    },
  ): Promise<{ success: true } | { success: false; message: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`contacts/company/${companyUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData),
        credentials: 'include',
      })

      if (response !== false && response.ok) return { success: true }
      let message = 'Erreur lors de la création du contact.'
      if (response !== false && !response.ok) {
        try {
          const data = await response.json()
          message = data?.message ?? message
        } catch {
          message = response.statusText || message
        }
      }
      return { success: false, message }
    } catch (error) {
      console.error('Erreur lors de la création du contact avec société:', error)
      return { success: false, message: (error as Error)?.message ?? 'Erreur lors de la création du contact.' }
    }
  }

  return {
    contacts,
    isLoadingContacts,
    currentContact,
    isLoadingContact,
    fetchContacts,
    fetchContact,
    updateContact,
    fetchBrevoEmails,
    sendWelcomeEmail,
    sendPasswordForgotEmail,
    fetch2FAStatus,
    reset2FA,
    disable2FA,
    fetchResetTokens,
    fetchPermissions,
    setPermissions,
    searchCompanies,
    searchContacts,
    createSuiteContact,
    generateTricksterToken,
  }
})
